import React, { useState, useRef, useEffect } from 'react';
import '../styles/home.css';
import { PDFDocument } from 'pdf-lib';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import rangeSelectorIcon from '../assets/splitpdf.png';
import { addUsage } from './add-usage.js';
import JSZip from 'jszip';
import constants from '../constants.js';

const SplitPDF = () => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [submitValue, setSubmitValue] = useState('Split');
  const [fileSize, setFileSize] = useState(null);
  const fileInputRef = useRef(null);
  const [pageCount, setPageCount] = useState(0);
  const [splitOption, setSplitOption] = useState('everyN');
  const [fromPage, setFromPage] = useState('');
  const [toPage, setToPage] = useState('');
  const [nPages, setNPages] = useState('');
  const [multiRanges, setMultiRanges] = useState([{ from: '', to: '' }]);
  const [fileNameFormat, setFileNameFormat] = useState('%[FileName]_%[FromPage]_%[ToPage]');
  const [selectedOption, setSelectedOption] = useState('');
  const [notify, setNotify] = useState('');
  const [error, setError] = useState(null);
  const [rangeMsg, setRangeMsg] = useState('');
  const [options] = useState([
    { label: 'Date', value: 'Date' },
    { label: 'Time', value: 'Time' },
    { label: 'Month', value: 'Month' },
    { label: 'Year', value: 'Year' },
    { label: 'Total Pages', value: 'TotalPages' },
    { label: 'Filename', value: 'FileName' },
    { label: 'From Page', value: 'FromPage' },
    { label: 'To Page', value: 'ToPage' },
  ]);

  const toggleModal = () => {
    setVisible(!visible);
    setFileSize('');
    setFile('');
    setNPages('');
    setFromPage('');
    setToPage('');
    setMultiRanges([{ from: '', to: '' }]);
    setSubmitValue('Split');
    setFileNameFormat('%[FileName]_%[FromPage]_%[ToPage]');
    setSelectedOption('');
    setSplitOption('everyN');
    setPageCount('');
    setNotify('');
    setError(null);
    fileInputRef.current.value = null;
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileSize(selectedFile.size);
      setNotify('');
      setError(null);

      try {
        const totalPageCount = await getTotalPageCount(selectedFile);
        setPageCount(totalPageCount);
      } catch (error) {
        console.error('Error loading PDF:', error);
        setNotify(`"${selectedFile.name}" could not be processed. Please check if it's a valid PDF.`);
        setError('error');
        setFile(null);
        setFileSize(null);
        setPageCount(0);
      }
    }
  };

  const getTotalPageCount = async (file) => {
    try {
      const pdfBytes = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      return pdfDoc.getPageCount();
    } catch (error) {
      const { handleEncryptedPDF } = await import('./pdf-encryption-handler.js');
      const processedPdfBytes = await handleEncryptedPDF(file);
      const pdfDoc = await PDFDocument.load(processedPdfBytes);
      return pdfDoc.getPageCount();
    }
  };

  const handlePositiveNumbers = (e, setter, pageCount) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      if (value === '0') {
        setter('1');
      } else {
        setter(value);
        if (value && parseInt(value) > pageCount) {
          setter(pageCount.toString());
        }
      }
    } else {
      setter('');
    }
  };

  const handleKeyDown = (e, setter, pageCount, compareValue) => {
    setter((prev) => {
      let currentValue = prev ? parseInt(prev) : 1;
      if (e.key === 'ArrowUp') {
        if (compareValue === 'fromPage') {
          currentValue = Math.max(currentValue, parseInt(fromPage || '1'));
        }
        const newValue = currentValue + 1;
        return newValue > pageCount ? pageCount.toString() : newValue.toString();
      } else if (e.key === 'ArrowDown') {
        const newValue = currentValue - 1;
        return newValue < (compareValue === 'fromPage' ? parseInt(fromPage || '1') : 1) ? '1' : newValue.toString();
      }
    });
  };

  useEffect(() => {
    if (fromPage && toPage && parseInt(fromPage) > parseInt(toPage)) {
      setFromPage(toPage);
    }
  }, [toPage]);

  useEffect(() => {
    if (fromPage && toPage && parseInt(toPage) < parseInt(fromPage)) {
      setToPage(fromPage);
    }
  }, [fromPage]);

  const handleMultiRangeChange = (index, field, value) => {
    if (/^\d*$/.test(value)) {
      const newRanges = [...multiRanges];
      let val = value === '0' ? '1' : value;
      if (val && parseInt(val) > pageCount) val = pageCount.toString();
      newRanges[index][field] = val;
      setMultiRanges(newRanges);
    }
  };

  const addRange = () => {
    const lastRange = multiRanges[multiRanges.length - 1];
    if (lastRange.from && lastRange.to) {
      setMultiRanges([...multiRanges, { from: '', to: '' }]);
      setRangeMsg('');
    } else {
      setRangeMsg('Fill current range first');
    }
  };
  const removeRange = (index) => setMultiRanges(multiRanges.filter((_, i) => i !== index));

  const handleSelectOption = (option) => {
    if (option) {
      const formattedOption = `%[${option}]`;
      if (!fileNameFormat.includes(formattedOption)) {
        setFileNameFormat((prev) => (prev ? `${prev}_${formattedOption}` : formattedOption));
      }
      setSelectedOption('');
    }
  };

  const extractPDFRange = async () => {
    setSubmitValue(<Spinner />);
    if (!file) return;

    try {
      const pdfBytes = await file.arrayBuffer();
      let pdfDoc;
      try {
        pdfDoc = await PDFDocument.load(pdfBytes);
      } catch (e) {
        const { handleEncryptedPDF } = await import('./pdf-encryption-handler.js');
        const processedPdfBytes = await handleEncryptedPDF(file);
        pdfDoc = await PDFDocument.load(processedPdfBytes);
      }
      const newPdfDoc = await PDFDocument.create();
      const zip = new JSZip();
      const originalFileName = file.name.split('.').slice(0, -1).join('.');

      if (splitOption === 'fromTo') {
        const from = parseInt(fromPage);
        const to = parseInt(toPage);
        const copiedPages = await newPdfDoc.copyPages(
          pdfDoc,
          Array.from({ length: to - from + 1 }, (_, i) => from - 1 + i),
        );
        copiedPages.forEach((page) => newPdfDoc.addPage(page));
        const newPdfBytes = await newPdfDoc.save();
        downloadFile(newPdfBytes, generateFileName(), false);
      } else if (splitOption === 'multiRange') {
        for (const range of multiRanges) {
          const from = parseInt(range.from);
          const to = parseInt(range.to);
          const rangeDoc = await PDFDocument.create();
          const copiedPages = await rangeDoc.copyPages(
            pdfDoc,
            Array.from({ length: to - from + 1 }, (_, i) => from - 1 + i),
          );
          copiedPages.forEach((page) => rangeDoc.addPage(page));
          const pdfBytes = await rangeDoc.save();
          zip.file(generateFileName(from, to), pdfBytes);
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        downloadFile(zipBlob, `${originalFileName}_ranges.zip`, true);
      } else if (splitOption === 'everyN') {
        const everyN = parseInt(nPages);
        let currentPage = 0;
        while (currentPage < pageCount) {
          const newDoc = await PDFDocument.create();
          const copiedPages = await newDoc.copyPages(
            pdfDoc,
            Array.from({ length: Math.min(everyN, pageCount - currentPage) }, (_, i) => currentPage + i),
          );
          copiedPages.forEach((page) => newDoc.addPage(page));
          const newPdfBytes = await newDoc.save();
          const fileName = generateFileName(currentPage + 1, Math.min(currentPage + everyN, pageCount));
          zip.file(`${fileName}.pdf`, newPdfBytes);
          currentPage += everyN;
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        downloadFile(zipBlob, `${originalFileName}.zip`, true);
      } else if (splitOption === 'oddPages') {
        const oddPages = Array.from({ length: pageCount }, (_, i) => i).filter(
          (pageNumber) => (pageNumber + 1) % 2 === 1,
        );
        const copiedPages = await newPdfDoc.copyPages(pdfDoc, oddPages);
        copiedPages.forEach((page) => newPdfDoc.addPage(page));
        const newPdfBytes = await newPdfDoc.save();
        downloadFile(newPdfBytes, generateFileName(), false);
      } else if (splitOption === 'evenPages') {
        const evenPages = Array.from({ length: pageCount }, (_, i) => i).filter(
          (pageNumber) => (pageNumber + 1) % 2 === 0,
        );
        const copiedPages = await newPdfDoc.copyPages(pdfDoc, evenPages);
        copiedPages.forEach((page) => newPdfDoc.addPage(page));
        const newPdfBytes = await newPdfDoc.save();
        downloadFile(newPdfBytes, generateFileName(), false);
      }

      const outputCount = splitOption === 'everyN' ? Math.ceil(pageCount / parseInt(nPages)) : splitOption === 'multiRange' ? multiRanges.length : 1;
      addUsage('AWESOME PDF TOOLS', '1.0', outputCount, window.location.href);
      toggleModal();
    } catch (error) {
      console.error('Split error:', error);
      try {
        const { handleEncryptedPDF } = await import('./pdf-encryption-handler.js');
        const processedPdfBytes = await handleEncryptedPDF(file);
        const pdfDoc = await PDFDocument.load(processedPdfBytes);
        // Retry split logic with decrypted PDF...
        const outputCount = splitOption === 'everyN' ? Math.ceil(pageCount / parseInt(nPages)) : splitOption === 'multiRange' ? multiRanges.length : 1;
        addUsage('AWESOME PDF TOOLS', '1.0', outputCount, window.location.href);
        toggleModal();
      } catch (encryptionError) {
        setNotify(`"${file.name}" could not be processed. Please check if it's a valid PDF.`);
        setError('error');
        setSubmitValue('Split');
      }
    }
  };

  const downloadFile = (fileBlob, fileName, isZip) => {
    let downloadUrl;
    if (isZip === true) {
      downloadUrl = window.URL.createObjectURL(fileBlob);
    } else {
      downloadUrl = window.URL.createObjectURL(new Blob([fileBlob], { type: constants.pdfFileType }));
    }
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const generateFileName = (startPage = fromPage || '1', endPage = toPage || pageCount) => {
    let finalName = fileNameFormat
      .replace('%[FileName]', file?.name?.split('.')[0] || 'split_pdf')
      .replace('%[FromPage]', startPage)
      .replace('%[ToPage]', endPage)
      .replace('%[TotalPages]', pageCount)
      .replace('%[Date]', new Date().toLocaleDateString())
      .replace('%[Time]', new Date().toLocaleTimeString())
      .replace('%[Month]', new Date().getMonth() + 1)
      .replace('%[Year]', new Date().getFullYear());
    return `${finalName}.pdf`;
  };

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        <div className="service-card-icon">
          <img src={rangeSelectorIcon} alt="Split PDF" loading="lazy" />
        </div>
        <h3 className="service-card-title">Split PDF</h3>
        <p className="service-card-desc">Split a PDF into multiple parts using different methods</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="large"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggleModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={
                  !(
                    file &&
                    ((splitOption === 'fromTo' && fromPage && toPage) ||
                      (splitOption === 'everyN' && nPages) ||
                      (splitOption === 'multiRange' && multiRanges.every(r => r.from && r.to && parseInt(r.from) <= parseInt(r.to))) ||
                      splitOption === 'oddPages' ||
                      splitOption === 'evenPages') &&
                    !error
                  )
                }
                onClick={extractPDFRange}
              >
                {submitValue}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header="Split PDF"
      >
        <div className="split-pdf-container">
          <div className="bordered-box">
            <h6 className="container-header">PDF Source</h6>
            <FormField label="Drop file here or click to upload" errorText={notify}>
              <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileChange} />
            </FormField>
            {file && (
              <div className="file-info-display">
                <strong>File:</strong> {file.name} | <strong>Size:</strong> {(fileSize / 1024).toFixed(2)} KB |{' '}
                <strong>Pages:</strong> {pageCount}
              </div>
            )}
          </div>

          <div className="bordered-box">
            <h6 className="container-header">Splitting Method</h6>
            <div className="split-option">
              <label>
                <input
                  type="radio"
                  value="everyN"
                  checked={splitOption === 'everyN'}
                  onChange={() => setSplitOption('everyN')}
                />
                Split every
              </label>
              <input
                type="number"
                value={nPages}
                onChange={(e) => handlePositiveNumbers(e, setNPages, pageCount)}
                placeholder="N"
                className="number-input"
                disabled={splitOption !== 'everyN' || !file}
              />
              <span>page(s)</span>
            </div>
            <div className="split-option">
              <label>
                <input
                  type="radio"
                  value="fromTo"
                  checked={splitOption === 'fromTo'}
                  onChange={() => setSplitOption('fromTo')}
                />
                Page range: From
              </label>
              <input
                type="number"
                value={fromPage}
                onChange={(e) => handlePositiveNumbers(e, setFromPage, pageCount)}
                placeholder="1"
                className="number-input"
                disabled={splitOption !== 'fromTo' || !file}
              />
              <span>to</span>
              <input
                type="number"
                value={toPage}
                onChange={(e) => handlePositiveNumbers(e, setToPage, pageCount)}
                placeholder={pageCount || 'N'}
                className="number-input"
                disabled={splitOption !== 'fromTo' || !file}
              />
            </div>
            <div className="split-option">
              <label>
                <input
                  type="radio"
                  value="multiRange"
                  checked={splitOption === 'multiRange'}
                  onChange={() => setSplitOption('multiRange')}
                />
                Multiple ranges
              </label>
              <button
                type="button"
                onClick={addRange}
                disabled={splitOption !== 'multiRange' || !file}
                className="add-range-btn"
                title="Add range"
              >
                +
              </button>
              {rangeMsg && <span style={{ color: '#e88b00', fontSize: '12px', marginLeft: '8px' }}>{rangeMsg}</span>}
            </div>
            {splitOption === 'multiRange' && (
              <div className="multi-range-container">
                {multiRanges.map((range, index) => (
                  <div key={index} className="split-option multi-range-row">
                    <span>Range {index + 1}:</span>
                    <input
                      type="number"
                      value={range.from}
                      onChange={(e) => handleMultiRangeChange(index, 'from', e.target.value)}
                      placeholder="From"
                      className="number-input"
                      disabled={!file}
                    />
                    <span>to</span>
                    <input
                      type="number"
                      value={range.to}
                      onChange={(e) => handleMultiRangeChange(index, 'to', e.target.value)}
                      placeholder="To"
                      className="number-input"
                      disabled={!file}
                    />
                    {multiRanges.length > 1 && (
                      <button type="button" onClick={() => removeRange(index)} className="remove-range-btn">×</button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="split-option">
              <label>
                <input
                  type="radio"
                  value="oddPages"
                  checked={splitOption === 'oddPages'}
                  onChange={() => setSplitOption('oddPages')}
                />
                Extract odd pages only
              </label>
            </div>
            <div className="split-option">
              <label>
                <input
                  type="radio"
                  value="evenPages"
                  checked={splitOption === 'evenPages'}
                  onChange={() => setSplitOption('evenPages')}
                />
                Extract even pages only
              </label>
            </div>
          </div>

          <div className="bordered-box">
            <h6 className="container-header">Output Options</h6>
            <div className="output-options">
              <FormField label="Output filename format:">
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    value={fileNameFormat}
                    onChange={(e) => setFileNameFormat(e.target.value)}
                    style={{ flex: 1, padding: '10px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'monospace', fontSize: '14px' }}
                  />
                  <select
                    value={selectedOption}
                    onChange={(e) => handleSelectOption(e.target.value)}
                    style={{ padding: '8px', paddingHorizontal:150, border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', cursor: 'pointer', width: '10px' }}
                  >
                    <option value="">Insert...</option>
                    {options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </FormField>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SplitPDF;
